/**
 * Accepted / rejected / not-yet-reviewed classification for the pages filter,
 * at PHYSICAL PAGE granularity.
 *
 * `ocr_qa_page_reviews` holds one verdict per (page, tier) — written by the
 * per-page accept/reject and, on a container reject, for the pages the reviewer
 * actually ticked. A page with no row of its own was never reached, and reads as
 * "not yet reviewed" rather than inheriting the container's reject: sending the
 * operator to fix every page when one was named is the failure this avoids.
 *
 * `qa_reviews[].page_notes` remains a read-only fallback so containers rejected
 * before the per-page table existed still classify.
 *
 * `pageNumber` is the GLOBAL 1-based page number — the same space the panel's
 * page sections use, and the space both `page_number` and `page_notes` must be
 * keyed in. A verdict written under a different key (an ordinal into whatever
 * page list a dialog happened to render, say) leaves the page reading as
 * unreviewed while the record sits in a slot nothing looks at.
 */
export function pageQaBucket(
  result: {
    qa_page_reviews?:
      | {
          page_number: number;
          verdict?: string | null;
          superseded_at?: string | null;
        }[]
      | null;
    qa_reviews?:
      | {
          verdict?: string | null;
          page_notes?: Record<string, string> | null;
        }[]
      | null;
  },
  pageNumber: number
): 'accepted' | 'rejected' | 'pending' {
  // A page's own verdict is the authority, and it outranks the result-level
  // review deliberately: a reviewer who accepted this page and then rejected
  // the container has said two true things, and the accept is the one the
  // operator needs — it tells them which pages not to re-check.
  const own = (result.qa_page_reviews ?? []).find(
    (r) => r.page_number === pageNumber && !r.superseded_at
  );
  if (own?.verdict === 'accepted') return 'accepted';
  if (own?.verdict === 'rejected') return 'rejected';

  const reviews = result.qa_reviews ?? [];
  const rejectedReview = reviews.find((r) => r.verdict === 'rejected');

  if (rejectedReview) {
    const reason = rejectedReview.page_notes?.[String(pageNumber)];
    return reason && reason.trim().length > 0 ? 'rejected' : 'pending';
  }

  if (reviews.some((r) => r.verdict === 'accepted')) return 'accepted';

  return 'pending';
}
